import { FORM_OFFICE_API_VALUES, type FormOfficeApi } from "@/src/utils/tasks/normalizeFormOfficeForApi";
import type { AdmissionOfficeCountry } from "@/src/utils/admissionOffice/resolveAdmissionOfficeRouteFromTask";
import { toOfficeUrl, officeUrlsMatch } from "@/src/utils/accessControl/formatOfficeUrl";

/** Roles with unrestricted country / Admissions Office tab access. */
export const FULL_COUNTRY_ACCESS_ROLES = [
  "admin",
  "sub-admin",
  "application-hr",
  "documents-hr",
] as const;

export type FullCountryAccessRole = (typeof FULL_COUNTRY_ACCESS_ROLES)[number];

export const OFFICE_TO_ADMISSION_COUNTRY: Record<FormOfficeApi, AdmissionOfficeCountry> = {
  MALTA: "malta",
  UKRAINE: "ukraine",
  POLAND: "poland",
};

export const ADMISSION_COUNTRY_TO_OFFICE: Record<AdmissionOfficeCountry, FormOfficeApi> = {
  malta: "MALTA",
  ukraine: "UKRAINE",
  poland: "POLAND",
};

export const ALL_FORM_OFFICES: FormOfficeApi[] = [...FORM_OFFICE_API_VALUES];

/**
 * Source of truth: `form_office` → sites.
 * Add/remove domains here. One site may belong to several offices.
 * `All` is not part of this map.
 */
export const OFFICE_TO_SITES: Record<FormOfficeApi, string[]> = {
  UKRAINE: ["feu.com.ua"],
  POLAND: ["feu.com.pl"],
  MALTA: ["feu.com.pl"],
};

const collectUniqueSites = (officeSites: Record<FormOfficeApi, string[]>): string[] => {
  const seen = new Set<string>();
  const sites: string[] = [];
  (Object.values(officeSites) as string[][]).forEach((officeUrls) => {
    officeUrls.forEach((site) => {
      const canonical = toOfficeUrl(site);
      if (!canonical || seen.has(canonical)) return;
      seen.add(canonical);
      sites.push(canonical);
    });
  });
  return sites;
};

const buildSiteToOffices = (officeSites: Record<FormOfficeApi, string[]>): Record<string, FormOfficeApi[]> => {
  const map: Record<string, FormOfficeApi[]> = {};
  (Object.entries(officeSites) as [FormOfficeApi, string[]][]).forEach(([office, officeUrls]) => {
    officeUrls.forEach((site) => {
      const canonical = toOfficeUrl(site);
      if (!canonical) return;
      if (!map[canonical]) map[canonical] = [];
      if (!map[canonical].includes(office)) map[canonical].push(office);
    });
  });
  return map;
};

export const SIDEBAR_SITE_OPTIONS = collectUniqueSites(OFFICE_TO_SITES);
export const SITE_URL_TO_ALLOWED_OFFICES = buildSiteToOffices(OFFICE_TO_SITES);

export type SidebarSiteOption = (typeof SIDEBAR_SITE_OPTIONS)[number];

/** Map domain / full URL to the canonical stored `office_url` value (domain only). */
export const toCanonicalSidebarSiteUrl = (value?: string | null): string => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  return SIDEBAR_SITE_OPTIONS.find((option) => officeUrlsMatch(option, trimmed)) ?? "";
};

/** Country lock from selected `office_url`. `null` = site does not restrict countries (`All`). */
export const resolveOfficesFromSiteUrl = (siteUrl?: string | null): FormOfficeApi[] | null => {
  const canonical = toCanonicalSidebarSiteUrl(siteUrl);
  if (!canonical) return null;
  const offices = SITE_URL_TO_ALLOWED_OFFICES[canonical];
  return offices?.length ? [...offices] : null;
};

/**
 * Site is allowed for a locked role only when its mapped `form_office` matches.
 * `All` and unmapped sites do not count.
 */
export const isSidebarSiteCompatibleWithOffices = (
  siteUrl: string | null | undefined,
  allowedOffices: FormOfficeApi[],
): boolean => {
  const siteOffices = resolveOfficesFromSiteUrl(siteUrl);
  if (!siteOffices) return false;
  return siteOffices.some((office) => allowedOffices.includes(office));
};

/** True when the user cannot switch countries (one office only). */
export const isOfficeSwitcherLocked = (
  hasFullCountryAccess: boolean,
  allowedOffices: readonly string[],
): boolean => !hasFullCountryAccess && allowedOffices.length <= 1;

/** Next table office, or `null` if the current value is already valid. */
export const resolveSyncedTableOffice = (
  currentOffice: string,
  hasFullCountryAccess: boolean,
  allowedOffices: readonly string[],
): string | null => {
  if (hasFullCountryAccess || !allowedOffices.length) return null;
  if (allowedOffices.includes(currentOffice)) return null;
  return allowedOffices[0];
};

// =============================================================================
// TEMPORARY: All option (no site filter) — for convenient viewing of all data.
// To restore production behavior (no All, always first URL by index):
//   1) uncomment the "RESTORE WITHOUT All" blocks below
//   2) remove / comment the "TEMPORARY WITH All" blocks
// =============================================================================

// --- TEMPORARY WITH All ---
/** Display label for “no site filter” — maps to empty `selectedSiteUrl`. */
export const SIDEBAR_SITE_ALL_LABEL = "All";

/** Options shown in sidebar-office (All + domains, without protocol). */
export const SIDEBAR_SITE_SELECT_OPTIONS = [SIDEBAR_SITE_ALL_LABEL, ...SIDEBAR_SITE_OPTIONS] as const;

/** Default = All (empty string, no site filter). */
export const getDefaultSidebarSiteUrl = (): string => "";

/** Map stored/selected value ↔ autocomplete display text (domain only). */
export const toSidebarSiteDisplayValue = (siteUrl: string): string => {
  const trimmed = String(siteUrl ?? "").trim();
  return trimmed ? toOfficeUrl(trimmed) : SIDEBAR_SITE_ALL_LABEL;
};

export const fromSidebarSiteDisplayValue = (display: string): string => {
  const trimmed = String(display ?? "").trim();
  if (!trimmed || trimmed === SIDEBAR_SITE_ALL_LABEL) return "";
  return toCanonicalSidebarSiteUrl(trimmed);
};

/** Sidebar-office options visible for this role. Locked roles never see `All`. */
export const getVisibleSidebarSiteOptions = (
  hasFullCountryAccess: boolean,
  roleAllowedOffices: FormOfficeApi[],
): string[] => {
  if (hasFullCountryAccess) return [...SIDEBAR_SITE_SELECT_OPTIONS];
  return SIDEBAR_SITE_OPTIONS.filter((site) => isSidebarSiteCompatibleWithOffices(site, roleAllowedOffices));
};

/** Drop incompatible stored `office_url` (e.g. Malta role + feu.com.ua). */
export const sanitizeSelectedSiteUrlForRole = (
  siteUrl: string,
  hasFullCountryAccess: boolean,
  roleAllowedOffices: FormOfficeApi[],
): string => {
  if (hasFullCountryAccess) return siteUrl;
  if (isSidebarSiteCompatibleWithOffices(siteUrl, roleAllowedOffices)) return siteUrl;
  const [firstAllowed] = getVisibleSidebarSiteOptions(false, roleAllowedOffices);
  return firstAllowed ? toCanonicalSidebarSiteUrl(firstAllowed) : "";
};
// --- end TEMPORARY WITH All ---

// --- RESTORE WITHOUT All (production) ---
// export const SIDEBAR_SITE_SELECT_OPTIONS = [...SIDEBAR_SITE_OPTIONS] as const;
//
// /** Always the first available option by index — not a hardcoded URL. */
// export const getDefaultSidebarSiteUrl = (): SidebarSiteOption => SIDEBAR_SITE_OPTIONS[0];
//
// export const toSidebarSiteDisplayValue = (siteUrl: string): string =>
//   toOfficeUrl(String(siteUrl ?? "").trim() || getDefaultSidebarSiteUrl());
//
// export const fromSidebarSiteDisplayValue = (display: string): string => {
//   const trimmed = String(display ?? "").trim();
//   return toCanonicalSidebarSiteUrl(trimmed) || getDefaultSidebarSiteUrl();
// };
// --- end RESTORE WITHOUT All ---
